import PhotoUploadWidget from '../common/photoupload/PhotoUploadWidget';
import PhotoUploadOverlay from '../common/photoupload/PhotoUploadOverlay';
import type { UploadedPhotoPlaceholderProps } from '../common/photoupload/types.d.ts';

interface ProfilePhotoUploadContentProps {
    onPhotoUpload: (files: FileList | File[]) => void;
    uploadedPhoto: UploadedPhotoPlaceholderProps | null;
    onDeleteRequest: (photoId: string) => void;
    currentPhotoUrl: string | null;
    photoIsPublished: boolean;
    onRemovePhoto?: () => void;
}

const EXAMPLE_PHOTOS = Array.from({ length: 13 }, (_, i) => {
    return { src: `${secureBaseURL}images/teacher/example-profile-photos/example-teacher-${i + 1}.jpg`, alt: '' };
});

const GUIDELINES_URL = 'https://help.donorschoose.org/hc/en-us/articles/214087968-Guidelines-for-your-teacher-profile-photo';

const ProfilePhotoUploadContent = ({
    onPhotoUpload,
    uploadedPhoto,
    onDeleteRequest,
    currentPhotoUrl,
    photoIsPublished,
    onRemovePhoto,
}: ProfilePhotoUploadContentProps) => {
    const renderSubHeader = () => {
        return (photoIsPublished
            ? (
                <>
                    <h2>Are you looking your best?</h2>
                    <p className="subheader">Make sure your photo is friendly and professional</p>
                </>)
            : (
                <>
                    <h2>Put your best face forward</h2>
                    <p className="subheader">Choose a friendly and professional photo of yourself</p>
                </>));
    };
    const renderCurrentPhoto = () => {
        return <>
            {currentPhotoUrl && !uploadedPhoto && (
                <div className="profile-photo-wrapper">
                    <img src={currentPhotoUrl} alt=""/>
                </div>
            )}
        </>;
    };
    const renderPhotoUploadWidgetOrOverlay = () => {
        return <>
            {uploadedPhoto
                ? (<PhotoUploadOverlay photoUrl={uploadedPhoto.photoUrl}
                    shouldRender={uploadedPhoto.shouldRender}
                    shouldRenderLoadingDots={uploadedPhoto.isLoading}
                    photoIndex={uploadedPhoto.photoId}
                    onDeleteRequest={onDeleteRequest}/>)
                : (<PhotoUploadWidget onPhotoUpload={onPhotoUpload}
                    multiple={false}/>)
            }
        </>;
    };
    const renderRemovePhotoButton = () => {
        return <>
            {photoIsPublished && onRemovePhoto && (
                <button
                    type="button"
                    className="remove-photo-link text-link"
                    onClick={onRemovePhoto}
                >
                    Remove photo
                </button>
            )}
        </>;
    };
    const renderExamplePhotos = () => {
        return (<ul className="examples">
            {EXAMPLE_PHOTOS.map((photo) => (
                <li key={photo.src}>
                    <img src={photo.src} alt={photo.alt}/>
                </li>
            ))}
        </ul>);
    };
    return (
        <div className="profile-photo-upload-content">
            {renderSubHeader()}

            {renderCurrentPhoto()}

            {renderPhotoUploadWidgetOrOverlay()}

            <p className="text-discreet">JPEG, PNG, or HEIC &#8226; At least 272 &times; 272 pixels</p>

            {renderRemovePhotoButton()}

            {renderExamplePhotos()}

            <p className="text-discreet">
                We will remove your photo if it does not feature your face or fit{' '}
                <a className="link-discreet" target="_blank" rel="noopener noreferrer" href={GUIDELINES_URL}>
                    our guidelines
                </a>
            </p>
        </div>
    );
};

export default ProfilePhotoUploadContent;
